import { createSlice } from '@reduxjs/toolkit';
import { AppDispatchType } from './Store';
import IAppStoreState from '../interfaces/IAppStoreState';
import IBibleStudyStoreState from '../interfaces/IBibleStudyStoreState';
import ReadingPlanRecordService from '../services/ReadingPlanRecordService';
import { DefaultBibleSettings } from '../data/DefaultBibleSettings';
import DateHelperService from '../services/DateHelperService';
import IReadingPlanSession from '../interfaces/IReadingPlanSession';
import IReadingPlanRecord from '../interfaces/IReadingPlanRecord';
import BibleSettingsService from '../services/BibleSettingsService';
import IReadingPlanInfo from '../interfaces/IReadingPlanInfo';
import NoActivePlanURL from '../data/NoActivePlanURL';
import BibleColorThemes from '../data/BibleColorThemes';

const BibleStudyInitialState: IBibleStudyStoreState = {
  currentSession: 1, // obsolete
  scheduledSession: 0, // obsolete
  nextUnreadURL: '',
  scheduledURL: '',
  bibleColorTheme: BibleColorThemes.default,
  bibleTabActive: false,
} 

const BibleStudySlice = createSlice({
  name: 'BibleStudy',
  initialState: BibleStudyInitialState,
  reducers: {
    UpdateCurrentSession: (state, action) => {
      state.currentSession = action.payload;
      return state;
    },
    UpdateScheduledSession: (state, action) => {
      state.scheduledSession = action.payload;
      return state;
    },
    UpdateSessions: (state, action) => {
      state.currentSession = action.payload.currentSession;
      state.scheduledSession = action.payload.scheduledSession;
      return state;
    },
    UpdateNextUnreadURL: (state, action) => {
      state.nextUnreadURL = action.payload;
      return state;
    },
    UpdateScheduledURL: (state, action) => {
      const url: string = action.payload.toString();
      const scheduled = url.match(/(\/)\d*(?=\/)/);
      console.log(scheduled);
      state.scheduledSession = scheduled ? Number.parseInt(scheduled[0].replace(/(\/)/, '')) : 0;
      state.scheduledURL = action.payload;
      return state;
    },
    UpdateLastVersion: (state, action) => {
      state.lastVersion = action.payload;
      return state;
    },
    UpdateLastViewedURL: (state, action) => {
      state.lastViewedURL = action.payload;
      return state;
    },
    UpdateActiveSession: (state, action) => {
      state.activeSession = DateHelperService.convertDatesToNumbers(action.payload) as IReadingPlanSession;
      return state;
    },
    UpdateActiveReadingPlanRecord: (state, action) => {
      state.activeReadingPlanRecord = DateHelperService.convertDatesToNumbers(action.payload) as IReadingPlanRecord;
      return state;
    },
    UpdateReadingPlans: (state, action) => {
      const plans = [...action.payload] as IReadingPlanInfo[];
      state.ReadingPlans = plans.map(plan => DateHelperService.convertDatesToNumbers(plan));
      return state;
    },
    UpdateSelectedVerse: (state, action) => {
      state.selectedVerse = action.payload;
      return state;
    },
    UpdateBibleColorTheme: (state, action) => {
      state.bibleColorTheme = action.payload;
      return state;
    },
    UpdateBibleTabActive: (state, action) => {
      state.bibleTabActive = action.payload;
      return state;
    },
  }
});

const { actions, reducer } = BibleStudySlice;

//Actions Export
export const { 
  UpdateCurrentSession, 
  UpdateScheduledSession, 
  UpdateSessions,
  UpdateNextUnreadURL,
  UpdateScheduledURL,
  UpdateLastVersion,
  UpdateLastViewedURL,
  UpdateActiveSession,
  UpdateActiveReadingPlanRecord,
  UpdateReadingPlans,
  UpdateSelectedVerse,
  UpdateBibleColorTheme,
  UpdateBibleTabActive,
} = actions;

//Selector Export
export const SelectBibleColorTheme = (state: IAppStoreState) => state.BibleStudy.bibleColorTheme;
export const SelectBibleTabActive = (state: IAppStoreState) => state.BibleStudy.bibleTabActive;
export const SelectCurrentSession = (state: IAppStoreState) => state.BibleStudy.currentSession;
export const SelectScheduledSession = (state: IAppStoreState) => state.BibleStudy.scheduledSession;
export const SelectScheduledURL = (state: IAppStoreState) => state.BibleStudy.scheduledURL;
export const SelectNextUnreadURL = (state: IAppStoreState) => state.BibleStudy.nextUnreadURL;
export const SelectLastVersion = (state: IAppStoreState) => state.BibleStudy.lastVersion;
export const SelectLastViewedURL = (state: IAppStoreState) => state.BibleStudy.lastViewedURL;
export const SelectActiveSession = (state: IAppStoreState) => DateHelperService.convertNumbersToDates(state.BibleStudy.activeSession) as IReadingPlanSession;
export const SelectActiveReadingPlanRecord = (state: IAppStoreState) => DateHelperService.convertNumbersToDates(state.BibleStudy.activeReadingPlanRecord) as IReadingPlanRecord;
export const SelectReadingPlans = (state: IAppStoreState) => state.BibleStudy.ReadingPlans as IReadingPlanInfo[];
export const SelectSelectedVerse = (state: IAppStoreState) => state.BibleStudy.selectedVerse;
export const SelectIsGroupPlanAdmin = (state: IAppStoreState) => (!state.BibleStudy.activeReadingPlanRecord?.groupId ||
  (state.Fellowship?.group?.creatorId === state.UserAccount?.User?.id && 
  state.BibleStudy.activeReadingPlanRecord?.groupId === state.Fellowship?.group?.id ));

//Reducer Export
export default reducer;

export const UpdateActivePlanByIdAsync = async(dispatch: AppDispatchType, groupIds: string[], recordId?: number) => {
  let activeReadingPlanId = recordId;

  // Get in redux and update stored active reading plan in db
  const settings = await BibleSettingsService.getBibleSettings().then(result => {
    if(result.Success) {
      const updatedBibleSettings = recordId !== undefined ? {...result.Data!, activeReadingPlanId: recordId} : {...result.Data!};
      activeReadingPlanId = updatedBibleSettings.activeReadingPlanId;
      // Set the active plan in long term storage for the next time the app opens.
      BibleSettingsService.updateBibleSettings(updatedBibleSettings);
      return updatedBibleSettings;
    } else {
      BibleSettingsService.createBibleSettings(DefaultBibleSettings).then(result => {
        if(result.Success) {
          const updatedBibleSettings = recordId !== undefined ? {...result.Data!, activeReadingPlanId: recordId} : {...result.Data!};
          activeReadingPlanId = updatedBibleSettings.activeReadingPlanId;
          // Set the active plan in long term storage for the next time the app opens.
          BibleSettingsService.updateBibleSettings(updatedBibleSettings);
          return updatedBibleSettings;
        }
      });
    }
  });

  if(settings) {
    const record = await ReadingPlanRecordService.getReadingPlanRecords().then(result => {
      if(result.Success) {
        const userPlans = result.Data!.filter(plan => !plan.groupId || groupIds.find(id => plan.groupId === id));
        const plans = userPlans.map(plan => {
          const {id, readingPlan, modifiedDate, readingPlanCode, groupId, name} = plan;
          return {id, readingPlan, modifiedDate, readingPlanCode, groupId, name} as IReadingPlanInfo;
        });
        dispatch(UpdateReadingPlans(plans));
        // Verify that the settings active reading plan is still available incase it was a group plan that's not available anymore
        let activePlan = userPlans.find(record => record.id === settings?.activeReadingPlanId);

        // If there is no active plan (potentially because it was a group plan and the plan is no longer available), find the next available plan
        if(!activePlan) {
          activePlan = userPlans.length ? userPlans[0] : undefined;
          const updatedBibleSettings = activePlan !== undefined ? {...settings, activeReadingPlanId: activePlan.id} : {...settings};
          dispatch(UpdateLastViewedURL(''));
          // Set the active plan in long term storage for the next time the app opens.
          BibleSettingsService.updateBibleSettings(updatedBibleSettings);
        }

        activeReadingPlanId = activePlan?.id;

        return activePlan;
      } else {
        return undefined;
      }
    });

    dispatch(UpdateActiveReadingPlanRecord(record));

    const unreadURL = record ? await ReadingPlanRecordService.nextUnreadSessionURL!(record.id).then(url => {
      const urlVersion = `${url}/${settings.defaultVersion}`;
      return urlVersion
    }) : NoActivePlanURL;
    dispatch(UpdateNextUnreadURL(unreadURL));

    const scheduledURL = record ? await ReadingPlanRecordService.scheduledSessionURL!(record.id).then(url => {
      const urlVersion = `${url}/${settings.defaultVersion}`;
      return urlVersion;
    }) : NoActivePlanURL;
    dispatch(UpdateScheduledURL(scheduledURL));
 
    dispatch(UpdateLastVersion(settings.defaultVersion));

    return { settings, record, version: settings.defaultVersion, scheduledURL, unreadURL }
  }
};
